import type { AuthError } from "firebase/auth";
import {
	AuthErrorCodes,
	signInWithEmailAndPassword,
	signInWithPopup,
} from "firebase/auth";
import { addDoc, collection, getDocs, query, where } from "firebase/firestore";
import { type ComponentType, useEffect, useState } from "react";
// @ts-expect-error - workaround for local react-hook-form type resolution issue
import { useForm } from "react-hook-form";
import { BsGoogle } from "react-icons/bs";
import { FiLogIn } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import validator from "validator";
import CustomButton from "../../components/custom-button/custom-button.component";
import CustomInput from "../../components/custom-input/custom-input.component";
import Header from "../../components/header/header.component";
import InputErrorMessage from "../../components/input-error-message/input-error-message.component";
import Loading from "../../components/loading/loading.component";
import { auth, db, googleProvider } from "../../config/firebase.config";
import { useAppSelector } from "../../hooks/redux.hooks";
import {
	LoginContainer,
	LoginContent,
	LoginHeadline,
	LoginInputContainer,
	LoginSubtitle,
} from "./login.styles";

interface LoginForm {
	email: string;
	password: string;
}

const LoginPage = () => {
	const FiLogInIcon = FiLogIn as unknown as ComponentType<{ size?: number }>;
	const BsGoogleIcon = BsGoogle as unknown as ComponentType<{ size?: number }>;

	const navigate = useNavigate();

	const {
		register,
		setError,
		formState: { errors },
		handleSubmit,
	} = useForm<LoginForm>();

	const [isLoading, setIsLoading] = useState(false);

	const { isAuthenticated } = useAppSelector(
		(rootState) => rootState.userReducer,
	);

	useEffect(() => {
		if (isAuthenticated) {
			navigate("/");
		}
	}, [isAuthenticated, navigate]);

	const handleSubmitPress = async (data: LoginForm) => {
		try {
			setIsLoading(true);
			const userCredentials = await signInWithEmailAndPassword(
				auth,
				data.email,
				data.password,
			);
			console.log(userCredentials);
		} catch (error) {
			const _error = error as AuthError;
			console.log(_error);
			if (_error.code === AuthErrorCodes.INVALID_PASSWORD) {
				return setError("password", { type: "invalid" });
			}

			if (_error.code === AuthErrorCodes.USER_DELETED) {
				return setError("email", { type: "notFound" });
			}
		} finally {
			setIsLoading(false);
		}
	};

	const handleGoogleSignIn = async () => {
		try {
			setIsLoading(true);
			const userCredentials = await signInWithPopup(auth, googleProvider);
			const querySnapshot = await getDocs(
				query(
					collection(db, "users"),
					where("id", "==", userCredentials.user.uid),
				),
			);
			const user = querySnapshot.docs[0]?.data();

			if (!user) {
				const firstName = userCredentials.user.displayName?.split(" ")[0] || "";
				const lastName =
					userCredentials.user.displayName?.split(" ").slice(1).join("") || "";
				await addDoc(collection(db, "users"), {
					id: userCredentials.user.uid,
					email: userCredentials.user.email,
					firstName,
					lastName,
					provider: "google",
				});
				console.log("Novo usuário criado no Firestore");
			}
		} catch (error) {
			console.error("Error signing in with Google:", error);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<>
			<Header />
			{isLoading && <Loading message="Entrando, por favor aguarde..." />}
			<LoginContainer>
				<LoginContent>
					<LoginHeadline>
						Bem-vindo de volta! Faça login para continuar.
					</LoginHeadline>
					<CustomButton
						startIcon={<BsGoogleIcon size={18} />}
						onClick={handleGoogleSignIn}
					>
						Entrar com Google
					</CustomButton>
					<LoginSubtitle>Ou entre com o seu e-mail</LoginSubtitle>
					<LoginInputContainer>
						<p>E-mail</p>
						<CustomInput
							type="email"
							hasError={!!errors?.email}
							placeholder="Digite seu e-mail"
							{...register("email", {
								required: true,
								validate: (value: string) => {
									return validator.isEmail(value);
								},
							})}
						/>
						{errors?.email?.type === "required" && (
							<InputErrorMessage>O e-mail é obrigatório.</InputErrorMessage>
						)}

						{errors?.email?.type === "invalid" && (
							<InputErrorMessage>
								O e-mail não foi encontrado.
							</InputErrorMessage>
						)}

						{errors?.email?.type === "validate" && (
							<InputErrorMessage>
								Por favor, insira um e-mail válido.
							</InputErrorMessage>
						)}
					</LoginInputContainer>
					<LoginInputContainer>
						<p>Senha</p>
						<CustomInput
							hasError={!!errors?.password}
							type="password"
							placeholder="Digite sua senha"
							{...register("password", { required: true })}
						/>
						{errors?.password?.type === "required" && (
							<InputErrorMessage>A senha é obrigatória.</InputErrorMessage>
						)}

						{errors?.password?.type === "invalid" && (
							<InputErrorMessage>A senha é inválida.</InputErrorMessage>
						)}
					</LoginInputContainer>
					<CustomButton
						startIcon={<FiLogInIcon size={18} />}
						onClick={() => handleSubmit(handleSubmitPress)()}
					>
						Entrar
					</CustomButton>
				</LoginContent>
			</LoginContainer>
		</>
	);
};

export default LoginPage;
